import React from 'react';

/**
 * KairosNameExplanation - A reusable component that displays the explanation
 * of what "Kairos" means and why the name was selected.
 * 
 * This component ensures consistent content and styling across all places
 * where the Kairos name explanation is displayed.
 * 
 * @example
 * ```tsx
 * <PopupTooltip content={<KairosNameExplanation />}>
 *   <img src={kairosLogo} alt="Kairos" />
 * </PopupTooltip>
 * ```
 */
export const KairosNameExplanation: React.FC = () => {
  return (
    <div>
      <strong style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.95rem' }}>
        What is Kairos?
      </strong>
      <p style={{ margin: '0 0 0.375rem 0', lineHeight: '1.4', fontSize: '0.8125rem' }}>
        <strong>Kairos</strong> is an ancient Greek word for the opportune moment. Not just any moment, but the one where preparation and timing line up. Ancient writers used it for the instant an archer finds a small opening, or a sailor catches the right wind.
      </p>
      <p style={{ margin: '0 0 0.375rem 0', lineHeight: '1.4', fontSize: '0.8125rem' }}>
        That is the heart of modern job search. Roles open. Teams shift. Windows close. Kairos helps you move when the odds are highest. It sources roles, scores fit, and watches for the signals that hiring momentum is real.
      </p>
      <p style={{ margin: 0, lineHeight: '1.4', fontSize: '0.8125rem' }}>
        When a window opens, it tailors your resume to that role so the right story meets the right moment. The name is a promise. Less guessing. Less noise. More well-timed action.
      </p>
    </div>
  );
};

/**
 * Default configuration for PopupTooltip when displaying Kairos name explanation
 * Use these values to ensure consistent tooltip behavior across the application
 */
export const KAIROS_NAME_EXPLANATION_CONFIG = {
  placement: 'bottom' as const,
  offset: 12,
  maxWidth: '900px',
  showDelay: 300,
  hideDelay: 150,
};

